import { useState, useCallback } from "react";
import { FileImage, Upload, Download, Trash2, FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import axios from "axios";
import SEOHead from "@/components/SEOHead";
import FAQSection from "@/components/FAQSection";
import RelatedTools from "@/components/RelatedTools";
import ToolContentSection from "@/components/ToolContentSection";
import { toolContent } from "@/data/toolContent";
import { downloadFile } from "@/utils/downloadHelper";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const faqs = [
  { question: "What does 'scanned effect' do?", answer: "It makes your PDF look like it was printed and scanned, adding subtle imperfections." },
  { question: "Why would I want this effect?", answer: "Some situations require scanned-looking documents, or you may want to add an authentic vintage feel." },
  { question: "Can I choose color or black & white?", answer: "Yes, choose between B&W (classic scan look) or color (retains colors with scan effect)." },
  { question: "Is this tool free?", answer: "Yes, completely free with no signup required." },
  { question: "Are my files secure?", answer: "Yes, files auto-delete after 30 minutes." }
];

const PdfScannedPage = () => {
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("bw");
  const [noiseLevel, setNoiseLevel] = useState([15]);
  const [rotation, setRotation] = useState([0.5]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === "application/pdf") {
      setFile(droppedFile);
      setResult(null);
    } else {
      toast.error("Please upload a PDF file");
    }
  }, []);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === "application/pdf") {
      setFile(selectedFile);
      setResult(null);
    } else {
      toast.error("Please upload a PDF file");
    }
  };

  const handleApplyEffect = async () => {
    if (!file) {
      toast.error("Please select a PDF file first");
      return;
    }

    setIsProcessing(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("mode", mode);
    formData.append("noise_level", noiseLevel[0]);
    formData.append("rotation_variance", rotation[0]);

    try {
      const response = await axios.post(`${API}/pdf-scanned-effect`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        setResult(response.data);
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Effect application failed");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = async () => {
    if (result?.files?.length > 0) {
      try {
        const downloadUrl = `${API}/download/${result.files[0].id}`;
        await downloadFile(downloadUrl, result.files[0].name || "scanned.pdf");
      } catch (error) {
        toast.error("Download failed");
      }
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
  };

  return (
    <>
      <SEOHead
        title="Make PDF Look Scanned Free Online | Scan Effect"
        description="Make your PDF look like a scanned document. Add realistic scan effect in B&W or color. Free online tool. No signup required."
        canonicalPath="/pdf-scanned"
        keywords="PDF scan effect, make PDF look scanned, scanned PDF effect, fake scan PDF, PDF to scanned look"
      />
      <div className="py-12 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8 animate-fade-in-up">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-black flex items-center justify-center">
                <FileImage className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-3xl md:text-5xl font-semibold tracking-tight">
                Make PDF Look Scanned Free
              </h1>
            </div>
            <p className="text-muted-foreground text-lg max-w-2xl">
              Add a realistic scanned effect to your PDF. Choose black & white or color mode.
              No signup required.
            </p>
          </div>

          {/* Upload Area */}
          {!result && (
            <div className="space-y-6 animate-fade-in-up animate-delay-100">
              <div
                className={`upload-zone ${isDragging ? "dragging" : ""}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById("file-input").click()}
                data-testid="upload-zone"
              >
                <input
                  id="file-input"
                  type="file"
                  accept=".pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="file-input"
              />
              {file ? (
                <div className="text-center">
                  <div className="w-16 h-16 bg-secondary flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-8 h-8 text-[#FF3B30]" />
                  </div>
                  <p className="font-medium text-lg">{file.name}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {(file.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                </div>
              ) : (
                <>
                  <div className="w-16 h-16 bg-secondary flex items-center justify-center">
                    <Upload className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <div className="text-center">
                    <p className="font-medium text-lg">Drop your PDF here</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      or click to browse (max 50MB)
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Options */}
            <div className="border border-border p-6 space-y-6">
              <div>
                <h3 className="font-medium mb-4">Scan Mode</h3>
                <RadioGroup
                  value={mode}
                  onValueChange={setMode}
                  className="flex gap-6"
                  data-testid="mode-radio"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="bw" id="bw" data-testid="mode-bw" />
                    <Label htmlFor="bw" className="cursor-pointer">
                      Black & White
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="color" id="color" data-testid="mode-color" />
                    <Label htmlFor="color" className="cursor-pointer">
                      Color
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <div className="flex justify-between mb-2">
                  <Label>Noise Level</Label>
                  <span className="text-sm text-muted-foreground">{noiseLevel[0]}</span>
                </div>
                <Slider
                  value={noiseLevel}
                  onValueChange={setNoiseLevel}
                  max={50}
                  min={0}
                  step={1}
                  className="w-full"
                  data-testid="noise-slider"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Add grain/noise for authentic scanned look
                </p>
              </div>

              <div>
                <div className="flex justify-between mb-2">
                  <Label>Rotation Variance</Label>
                  <span className="text-sm text-muted-foreground">{rotation[0]}°</span>
                </div>
                <Slider
                  value={rotation}
                  onValueChange={setRotation}
                  max={2}
                  min={0}
                  step={0.1}
                  className="w-full"
                  data-testid="rotation-slider"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Slight random rotation like a real scan
                </p>
              </div>
            </div>

            <Button
              onClick={handleApplyEffect}
              disabled={!file || isProcessing}
              className="w-full h-14 bg-black hover:bg-black/90 text-white rounded-sm btn-hover-lift text-lg font-medium"
              data-testid="apply-button"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Applying Effect...
                </>
              ) : (
                <>
                  <FileImage className="w-5 h-5 mr-2" />
                  Apply Scanned Effect
                </>
              )}
            </Button>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-6 animate-fade-in-up" data-testid="result-section">
            <div className="border border-border p-8 text-center">
              <div className="w-20 h-20 bg-[#FF3B30] flex items-center justify-center mx-auto mb-6">
                <FileText className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-semibold tracking-tight mb-2">
                Scanned Effect Applied
              </h2>
              <p className="text-muted-foreground mb-6">
                Your PDF now looks like a {mode === "bw" ? "black & white" : "color"} scanned document
              </p>
              <div className="flex justify-center gap-4">
                <Button
                  variant="outline"
                  onClick={handleReset}
                  className="rounded-sm"
                  data-testid="reset-button"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Process Another
                </Button>
                <Button
                  onClick={handleDownload}
                  className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 rounded-sm btn-hover-lift"
                  data-testid="download-button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
              </div>
            </div>

            <div className="bg-secondary p-4 text-center">
              <p className="text-sm text-muted-foreground">
                ⏱ File will be automatically deleted in 30 minutes
              </p>
            </div>
          </div>
        )}

        <ToolContentSection toolKey="pdf-scanned" content={toolContent["pdf-scanned"]} />
        <FAQSection faqs={faqs} />
        <RelatedTools currentTool="pdf-scanned" />
      </div>
    </div>
    </>
  );
};

export default PdfScannedPage;
